import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "./primitives.tsx";

export function Composer({
  onSubmit,
  onStop,
  busy,
  disabled,
  disabledReason,
}: {
  onSubmit: (text: string) => void;
  onStop: () => void;
  busy: boolean;
  /** An empty scope disables the composer — the alternative is asking a question
   *  nothing can answer and calling the refusal a result. */
  disabled?: boolean;
  disabledReason?: string;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const textarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const element = textarea.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 200)}px`;
  }, [value]);

  function send() {
    const text = value.trim();
    if (!text || busy || disabled) return;
    setValue("");
    onSubmit(text);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  }

  return (
    <div className="composer">
      <textarea
        ref={textarea}
        rows={1}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t("chat.placeholder")}
        aria-label={t("chat.placeholder")}
        disabled={disabled}
      />
      <div className="composer-row">
        <span className="hint">{disabled ? disabledReason : t("chat.hint")}</span>
        <span className="spacer" />
        {/* A 20s-plus agent run with no way out is a hostage situation, not a wait. */}
        {busy ? (
          <Button onClick={onStop}>{t("chat.stop")}</Button>
        ) : (
          <Button variant="primary" onClick={send} disabled={disabled || value.trim().length === 0}>
            {t("chat.send")}
          </Button>
        )}
      </div>
    </div>
  );
}
